import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as ReactLeaflet from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Customer } from './types';
import { PageBody } from '../../../elements/page-body';
import { useHttpClient } from '../../../shared/http-client';
import { useDrawer } from '../../../shared/drawer';
import { Area } from './types';
import { AreaForm } from './areas-form';
import { AreasList } from './areas-list';
import { getAreaColor } from './color-utils';
import { CustomersList } from './customers-list';
import { RouteModal } from './route-modal';
import { RouteSaveData } from './types';



export const CreateRoute = () => {
    const { t } = useTranslation() as { t: (key: string) => string };
    const { api, state } = useHttpClient();
    const { open, close } = useDrawer();
    const navigate = useNavigate();

    const [areas, setAreas] = useState<Area[]>([]);
    const [showAreas, setShowAreas] = useState<boolean>(true);
    const [selectedAreaId, setSelectedAreaId] = useState<number | null>(null);
    const [editingArea, setEditingArea] = useState<Area | null>(null);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [optimizing, setOptimizing] = useState<boolean>(false);
    const ignoreNextMapClickRef = useRef<boolean>(false);
    const lastErrorToastRef = useRef<string | null>(null);
    const [tempCircle, setTempCircle] = useState<{
        lat: number;
        lng: number;
        radius: number;
    } | null>(null);
    const [selectedRadius, setSelectedRadius] = useState<number>(10);
    const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
    const [routeCustomers, setRouteCustomers] = useState<Customer[]>([]);
    const [showRouteModal, setShowRouteModal] = useState<boolean>(false);

    const MapContainerAny = (ReactLeaflet as any).MapContainer;
    const TileLayerAny = (ReactLeaflet as any).TileLayer;
    const CircleAny = (ReactLeaflet as any).Circle;
    const useMapEvents = (ReactLeaflet as any).useMapEvents;
    const useMap = (ReactLeaflet as any).useMap;
    const MarkerAny = (ReactLeaflet as any).Marker;
    const PopupAny = (ReactLeaflet as any).Popup;

    const defaultCenter: [number, number] = [40.416775, -3.703790];

    // Custom icon for customers
    const customerIcon = L.divIcon({
        html: `
                <div style="
                    background-color: #006bc6;
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    border: 2px solid white;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                "></div>
            `,
        className: 'customer-marker',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
    });

    useEffect(() => {
        loadAreas();
    }, []);



    const loadAreas = () => {
        api('/fleet/areas/', 'GET');
    };

    const handleMapClick = (lat: number, lng: number) => {
        if (isCreating) {
            setTempCircle({ lat, lng, radius: selectedRadius });
        }
    };

    const handleSaveTempArea = () => {
        if (tempCircle) {
            open(
                t('common.label.nueva-area'),
                <AreaForm
                    area={{
                        center_lat: tempCircle.lat,
                        center_lon: tempCircle.lng,
                        radius: tempCircle.radius,
                        name: '',
                        description: ''
                    }}
                    onSave={saveArea}
                    onCancel={() => {
                        setTempCircle(null);
                        setIsCreating(false);
                        close();
                    }}
                />
            );
        }
    };

    const saveArea = (values: any) => {
        if (values.id) {
            api(`/fleet/areas/${values.id}/`, 'PATCH', values);
        } else {
            api('/fleet/areas/', 'POST', values);
        }
    };

    const handleEdit = (area: Area) => {
        setEditingArea(area);
        api(`/fleet/areas/${area.id}/customers/`, 'GET');
        console.log('area', area)

    };

    const handleSelectCustomer = (customer: Customer) => {
        setRouteCustomers((prev) => {
            const exists = prev.some((c) => (c.id != null && customer.id != null ? c.id === customer.id : c.name === customer.name));
            if (exists) {
                return prev;
            }
            return [...prev, customer];
        });
    };

    const handleRemoveRouteCustomer = (customer: Customer) => {
        setRouteCustomers((prev) =>
            prev.filter((c) => (c.id != null && customer.id != null ? c.id !== customer.id : c.name !== customer.name))
        );
    };

    const handleDelete = (id: number) => {
        if (window.confirm(t('common.modal.validate-delete'))) {
            api(`/fleet/areas/${id}/`, 'DELETE');
        }
    };

    const MapInteractionHandler = () => {
        useMapEvents({
            click: (e: any) => {
                if (ignoreNextMapClickRef.current) {
                    ignoreNextMapClickRef.current = false;
                    return;
                }
                setSelectedAreaId(null);
                setFilteredCustomers([]);
                setRouteCustomers([]);
                handleMapClick(e.latlng.lat, e.latlng.lng);
            }
        });
        return null;
    };

    const FitCustomersBounds = ({ customers }: { customers: Customer[] }) => {
        const map = useMap();

        useEffect(() => {
            const points = customers
                .map((c) => [Number(c.lat), Number(c.lon)] as [number, number])
                .filter(([lat, lon]) => Number.isFinite(lat) && Number.isFinite(lon));

            if (points.length === 0) {
                return;
            }

            const bounds = L.latLngBounds(points);
            map.fitBounds(bounds, { padding: [30, 30] });
        }, [customers, map]);

        return null;
    };

    const handleCreateRoute = () => {
        setShowRouteModal(true);
    };

    const handleSaveRoute = (data: RouteSaveData) => {
        console.log('Route data:', data);

        const obj = {
            jobs: data.customers,
            vehicles: [{ vehicle: data.vehicle.id }],
            ...(data.routeDate ? { date: data.routeDate } : {}),
            area: data.areaId,
            driver: data.driver?.id,
            default_driver_address: data.default_driver_address,
            ...(data.workday_start_time ? { workday_start_time: data.workday_start_time } : {}),
            ...(data.workday_end_time ? { workday_end_time: data.workday_end_time } : {}),
            ...(data.break_start_time ? { break_start_time: data.break_start_time } : {}),
            ...(data.break_end_time ? { break_end_time: data.break_end_time } : {}),
            ...(Number.isFinite(Number(data.break_duration_hours)) ? { break_duration_hours: Number(data.break_duration_hours) } : {}),
            ...(Number.isFinite(Number(data.estimated_visit_time_minutes)) ? { estimated_visit_time_minutes: Number(data.estimated_visit_time_minutes) } : {}),
            ...(Number.isFinite(Number(data.max_distance_per_day_km)) ? { max_distance_per_day_km: Number(data.max_distance_per_day_km) } : {})
        }

        setOptimizing(true);
        api('/fleet/optimize/', 'POST', obj);

        

        console.log('obj', obj)
        setShowRouteModal(false);
    };

    useEffect(() => {
        if (state.path?.includes('/fleet/areas/') && state.method === 'GET' && state.data) {
            if (Array.isArray(state.data.results)) {
                setAreas(state.data.results);
            }
        }
        if (state.path?.includes('/fleet/areas/') && state.path?.includes('/customers/')) {
            // This is the response for customers in a specific area
            if (Array.isArray(state.data)) {
                setFilteredCustomers(state.data);
            }
        }

        if (optimizing && state.path?.includes('/fleet/optimize/') && state.method === 'POST' && !state.isLoading) {
            setOptimizing(false);
            if(state.data?.result && Array.isArray(state.data.result)) {
                if(state.data?.result.length > 0) {
                    navigate(`/routes/${state.data?.result[0].id}`);
                }else{
                    toast.error(t('common.label.no-valid-routes'));
                }
            }
        }

        if (state.path?.includes('/fleet/areas/') && (state.method === 'POST' || state.method === 'PATCH')) {
            if (state.data) {
                toast.success(t('common.message.saved'));
                loadAreas();
                setIsCreating(false);
                setEditingArea(null);
                setTempCircle(null);
                close();
            }
        }

        if (state.path?.includes('/fleet/areas/') && state.method === 'DELETE') {
            if (state.data || state.data === null) {
                toast.success(t('common.message.deleted'));
                loadAreas();
            }
        }

        if (state.error) {
            if (optimizing) {
                setOptimizing(false);
            }
            const errorMsg = state.error?.details?.vehicles?.[0]?.non_field_errors?.[0];
            if (errorMsg?.includes('has no valid coordinates')) {
                if (lastErrorToastRef.current !== 'no-valid-coordinates') {
                    toast.error(t('common.label.no-valid-coordinates'));
                    lastErrorToastRef.current = 'no-valid-coordinates';
                }
            } else {
                if (lastErrorToastRef.current !== 'generic-error') {
                    toast.error(t('common.message.error'));
                    lastErrorToastRef.current = 'generic-error';
                }
            }
        } else {
            lastErrorToastRef.current = null;
        }
    }, [state, optimizing]);


    return (
        <PageBody title={''} noMargin={true}>
            <div>

                {optimizing && (
                    <div className="fixed inset-0 bg-black/30 flex items-center justify-center" style={{ zIndex: 9999 }}>
                        <div className="bg-white rounded-lg px-6 py-4 shadow-lg flex items-center gap-3">
                            <svg className="animate-spin h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <div className="text-sm text-gray-800">{t('common.loading')}</div>
                        </div>
                    </div>
                )}

                <div className="w-full flex flex-col p-4" style={{ height: 'calc(100vh - 120px)' }}>
                    <div className="pb-4">
                        <div className="bg-white/95 backdrop-blur border border-gray-200 rounded-lg shadow-sm px-3 py-2">
                            <div className="flex items-center justify-between gap-3">
                                <div className="text-lg font-semibold text-gray-800">
                                    {t('common.label.crear-ruta')} ({routeCustomers.length})
                                </div>
                                {routeCustomers.length > 0 && (
                                    <div className="flex items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={() => setRouteCustomers([])}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm"
                                        >
                                            {t('common.button.cancel')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleCreateRoute}
                                            className="px-5 py-2 text-sm font-semibold text-white bg-primary rounded-lg hover:bg-primary transition-all shadow-md hover:shadow-lg"
                                        >
                                            {t('common.label.crear-ruta')}
                                        </button>
                                    </div>
                                )}
                            </div>

                            {routeCustomers.length > 0 ? (
                                <div className="mt-2 flex flex-wrap gap-2 max-h-20">
                                    {routeCustomers.map((customer) => (
                                        <div
                                            key={customer.id ?? customer.name}
                                            className="flex items-center gap-2 px-2 py-1 rounded-full bg-blue-50 text-blue-900 border border-blue-200"
                                        >
                                            <span className="text-xs font-medium truncate max-w-[200px]">
                                                {customer.name}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveRouteCustomer(customer)}
                                                className="text-blue-700 hover:text-blue-900"
                                                aria-label="Remove"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="mt-2 text-xs text-gray-500">
                                    {t('common.label.anadir-a-la-ruta')}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex-1 flex gap-4 min-h-0">
                        <div className="flex-1 rounded-lg overflow-hidden shadow-lg border border-gray-200 relative">

                            <MapContainerAny
                                center={defaultCenter}
                                zoom={6}
                                style={{ height: '100%', width: '100%' }}
                                scrollWheelZoom={true}
                            >
                                <TileLayerAny
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />

                                {showAreas &&
                                    (selectedAreaId ? areas.filter(a => a.id === selectedAreaId) : areas).map((area) => (
                                        <CircleAny
                                            key={area.id}
                                            center={[area.center_lat, area.center_lon]}
                                            radius={area.radius * 1000}
                                            pathOptions={{
                                                color: getAreaColor(area.id),
                                                fillColor: getAreaColor(area.id),
                                                fillOpacity: 0.2,
                                                weight: 3
                                            }}
                                            eventHandlers={{
                                                click: (e: any) => {
                                                    if (e?.originalEvent) {
                                                        L.DomEvent.stop(e.originalEvent);
                                                    }
                                                    ignoreNextMapClickRef.current = true;
                                                    if (area.id != null) {
                                                        setSelectedAreaId(area.id);
                                                    }
                                                    setTimeout(() => handleEdit(area), 0);
                                                }
                                            }}
                                        >
                                        </CircleAny>
                                    ))}

                                {tempCircle && (
                                    <CircleAny
                                        center={[tempCircle.lat, tempCircle.lng]}
                                        radius={tempCircle.radius * 1000}
                                        pathOptions={{
                                            color: '#10b981',
                                            fillColor: '#10b981',
                                            fillOpacity: 0.3,
                                            weight: 3,
                                            dashArray: '10, 10'
                                        }}
                                    >
                                    </CircleAny>
                                )}

                                {/* Customers */}
                                {selectedAreaId != null &&
                                    filteredCustomers.map((customer) => (
                                        Number.isFinite(Number(customer.lat)) && Number.isFinite(Number(customer.lon)) && (
                                            <MarkerAny
                                                key={customer.id}
                                                position={[Number(customer.lat), Number(customer.lon)]}
                                                icon={customerIcon}
                                            >
                                                <PopupAny>
                                                    <div className="p-2 min-w-[200px]">
                                                        <h3 className="font-bold text-lg mb-2" style={{ color: '#006bc6' }}>
                                                            {customer.name}
                                                        </h3>
                                                        {customer.address && (
                                                            <p className="text-sm text-gray-600 mb-2">{customer.address}</p>
                                                        )}
                                                        <p className="text-xs text-gray-500 mb-3">
                                                            {t('common.label.coordenadas')}: {Number(customer.lat).toFixed(6)}, {Number(customer.lon).toFixed(6)}
                                                        </p>
                                                        <div className="mb-3">
                                                            <span className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                                                                customer.is_acquired 
                                                                    ? 'bg-green-100 text-green-800'
                                                                    : 'bg-blue-100 text-blue-800'
                                                            }`}>
                                                                {customer.is_acquired
                                                                    ? t('common.form-label.cliente')
                                                                    : t('common.label.prospecto')
                                                                }
                                                            </span>
                                                            <button
                                                                onClick={() => handleSelectCustomer(customer)}
                                                                className="ml-4 p-1.5 text-sm text-[#006bc6] hover:bg-blue-50 rounded transition-all"
                                                                title={t('common.label.anadir-a-la-ruta')}
                                                            >
                                                                {t('common.label.anadir-a-la-ruta')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </PopupAny>
                                            </MarkerAny>
                                        )
                                    ))}

                                {selectedAreaId != null && <FitCustomersBounds customers={filteredCustomers} />}

                                <MapInteractionHandler />
                            </MapContainerAny>
                        </div>

                        <div className="w-80 flex-shrink-0">
                            <AreasList
                                areas={areas}
                                selectedAreaId={selectedAreaId}
                                onSelectArea={(area) => {
                                    if (area.id != null) {
                                        setSelectedAreaId(area.id);
                                    }
                                }}
                                onEdit={handleEdit}
                                onDelete={handleDelete}
                            />
                        </div>
                        {/* Sidebar List */}
                        {selectedAreaId != null && (
                            <div className="w-80 flex-shrink-0">
                                <CustomersList
                                    customers={filteredCustomers}
                                    onAddToRoute={handleSelectCustomer}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {showRouteModal && (
                    <RouteModal
                        customers={routeCustomers}
                        areaId={selectedAreaId}
                        onClose={() => setShowRouteModal(false)}
                        onSave={handleSaveRoute}
                    />
                )}
            </div>

        </PageBody>
    );
};
